export const DEFECT_RESOLUTIONS = ['REPLACEMENT_PART', 'PARTIAL_REFUND', 'REFUND', 'STORE_CREDIT', 'CUSTOM'] as const;
export type DefectResolution = typeof DEFECT_RESOLUTIONS[number];
export const DEFECT_RESOLUTION_LABELS: Record<DefectResolution, string> = {
  REPLACEMENT_PART: 'Replacement part', PARTIAL_REFUND: 'Partial refund', REFUND: 'Full refund', STORE_CREDIT: 'Store credit', CUSTOM: 'Custom resolution',
};

export interface DefectResolutionPolicySettings {
  version: 1;
  enabled: boolean;
  instructions: string;
  reviewTargetBusinessDays: number;
  enabledResolutions: DefectResolution[];
  customerRequestableResolutions: DefectResolution[];
  requireCustomerAcceptance: boolean;
  partialRefundMaxPercent: number;
  partialRefundDefaultPercent: number;
  returnRequired: Partial<Record<DefectResolution, boolean>>;
}

export const defaultDefectResolutionPolicy = (): DefectResolutionPolicySettings => ({
  version: 1, enabled: false, instructions: '', reviewTargetBusinessDays: 3,
  enabledResolutions: [...DEFECT_RESOLUTIONS], customerRequestableResolutions: [],
  requireCustomerAcceptance: true, partialRefundMaxPercent: 100, partialRefundDefaultPercent: 25, returnRequired: {},
});

const object = (value: unknown): value is Record<string, any> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const resolution = (value: unknown): value is DefectResolution => DEFECT_RESOLUTIONS.includes(value as DefectResolution);
const date = (value: unknown): value is string => typeof value === 'string' && Number.isFinite(Date.parse(value));

export function isDefectResolutionPolicy(value: unknown): value is DefectResolutionPolicySettings {
  return object(value) && value.version === 1 && typeof value.enabled === 'boolean' && typeof value.instructions === 'string' &&
    Number.isInteger(value.reviewTargetBusinessDays) && value.reviewTargetBusinessDays >= 1 && value.reviewTargetBusinessDays <= 30 &&
    Array.isArray(value.enabledResolutions) && value.enabledResolutions.every(resolution) && (!value.enabled || value.enabledResolutions.length > 0) &&
    Array.isArray(value.customerRequestableResolutions) && value.customerRequestableResolutions.every((item: unknown) => resolution(item) && value.enabledResolutions.includes(item)) &&
    typeof value.requireCustomerAcceptance === 'boolean' && finite(value.partialRefundMaxPercent) && value.partialRefundMaxPercent > 0 && value.partialRefundMaxPercent <= 100 &&
    finite(value.partialRefundDefaultPercent) && value.partialRefundDefaultPercent > 0 && value.partialRefundDefaultPercent <= value.partialRefundMaxPercent &&
    object(value.returnRequired) && Object.entries(value.returnRequired).every(([key, required]) => resolution(key) && typeof required === 'boolean');
}

export type DefectPlanStatus = 'OFFERED' | 'ACCEPTED' | 'DECLINED' | 'WITHDRAWN' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'OUTCOME_UNKNOWN';
export interface DefectResolutionPlan {
  id: string;
  resolution: DefectResolution;
  status: DefectPlanStatus;
  title: string;
  customerMessage: string;
  internalNote?: string;
  amount?: number;
  currency?: string;
  returnRequired: boolean;
  acceptanceRequired: boolean;
  executionCanResume?: boolean;
  part?: { name: string; sku?: string; quantity: number; destination: string };
  createdAt: string;
  acceptedAt?: string;
  completedAt?: string;
  completion?: { reference?: string; carrier?: string; trackingNumber?: string; trackingUrl?: string; customerMessage?: string };
}

export interface DefectResolutionState {
  version: number;
  updatedAt: string;
  policy: DefectResolutionPolicySettings;
  capabilities: { exchange: false; automaticPartFulfillment: false };
  plan: DefectResolutionPlan | null;
  informationRequest: { question: string; status: 'OPEN' | 'ANSWERED'; answer?: string; requestedAt: string; answeredAt?: string } | null;
  history: Array<{ action: string; at: string; message?: string }>;
  recordStatus?: string;
  evidenceStatus?: string;
  evidenceRevision?: number;
  financialPreview?: { amount: number; currency: string } | null;
}

const statuses = ['OFFERED', 'ACCEPTED', 'DECLINED', 'WITHDRAWN', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'OUTCOME_UNKNOWN'];
export function isDefectResolutionState(value: unknown): value is DefectResolutionState {
  if (!object(value) || !Number.isInteger(value.version) || value.version < 0 || !date(value.updatedAt) || !isDefectResolutionPolicy(value.policy) ||
    !object(value.capabilities) || value.capabilities.exchange !== false || value.capabilities.automaticPartFulfillment !== false ||
    !Array.isArray(value.history) || !value.history.every((entry: unknown) => object(entry) && typeof entry.action === 'string' && date(entry.at) && (entry.message === undefined || typeof entry.message === 'string'))) return false;
  const plan = value.plan;
  if (plan !== null && (!object(plan) || typeof plan.id !== 'string' || !plan.id || !resolution(plan.resolution) || !statuses.includes(plan.status) ||
    typeof plan.title !== 'string' || typeof plan.customerMessage !== 'string' || typeof plan.returnRequired !== 'boolean' || typeof plan.acceptanceRequired !== 'boolean' || !date(plan.createdAt) ||
    (plan.executionCanResume !== undefined && typeof plan.executionCanResume !== 'boolean') ||
    (plan.internalNote !== undefined && typeof plan.internalNote !== 'string') ||
    (plan.amount !== undefined && (!finite(plan.amount) || plan.amount < 0)) || (plan.currency !== undefined && (typeof plan.currency !== 'string' || !/^[A-Z]{3}$/.test(plan.currency))) ||
    (plan.resolution === 'REPLACEMENT_PART' && (!object(plan.part) || typeof plan.part.name !== 'string' || !plan.part.name.trim() || !Number.isInteger(plan.part.quantity) || plan.part.quantity < 1 || typeof plan.part.destination !== 'string' || !plan.part.destination.trim() || plan.part.destination.length > 1000)) ||
    (plan.completion !== undefined && (!object(plan.completion) || Object.values(plan.completion).some((field) => typeof field !== 'string'))))) return false;
  const information = value.informationRequest;
  return information === null || (object(information) && typeof information.question === 'string' && ['OPEN', 'ANSWERED'].includes(information.status) && date(information.requestedAt) && (information.answer === undefined || typeof information.answer === 'string'));
}

export function floorDefectPercentageAmount(amount: number, percent: number, currencyDigits: number): number {
  const factor = 10 ** currencyDigits;
  const units = Math.round(amount * factor);
  if (typeof BigInt !== 'function' || !Number.isSafeInteger(units) || units < 0 || !Number.isInteger(currencyDigits) || currencyDigits < 0 ||
    !Number.isFinite(percent) || percent <= 0 || percent > 100) return 0;
  const decimal = /^(\d+)(?:\.(\d+))?$/.exec(String(percent));
  if (!decimal) return 0;
  const fraction = decimal[2] || '';
  const numerator = BigInt(units) * BigInt(`${decimal[1]}${fraction}`);
  const denominator = BigInt(100) * BigInt(`1${'0'.repeat(fraction.length)}`);
  return Number(numerator / denominator) / factor;
}

export function safeDefectTrackingUrl(value?: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

export function defectCommandKey(): string {
  const bytes = new Uint32Array(4);
  try {
    if (typeof globalThis === 'undefined' || typeof globalThis.crypto?.getRandomValues !== 'function') throw new Error();
    globalThis.crypto.getRandomValues(bytes);
  } catch {
    throw new Error('Secure request identifiers are unavailable. Use a current browser over HTTPS and try again.');
  }
  return `defect-${Array.from(bytes).map((item) => item.toString(16)).join('-')}`;
}
